/**
 * The zoomed-in view's map: the whole drawing in miniature in the stage's
 * bottom-left corner with the visible window drawn on it, shown only while
 * zoomed in. Click or drag to move the window (the pointer's map point
 * becomes the view center); a drag moves it only while a button is down. The
 * drag lives in the shown map (`ShownMinimap`), which mounts only while the
 * map shows, so a drag cut short as the map hides never outlives it to turn
 * a later hover, or a canvas drag crossing the map, into one.
 * Editor-core's minimapLayout decides what it shows and
 * viewportAtMinimapPoint where a press takes the view. The caller supplies
 * the drawing for a given thumbnail viewport, so the room editor and the
 * assembler each show their own content through one map.
 */

import { css } from '@emotion/react';
import { MINIMAP_MARGIN, type MinimapGeometry, minimapLayout, type Viewport, viewportAtMinimapPoint } from '@kaiizen/kai-floor-plan-editor-core';
import { useRef } from 'react';
import { ink, paper, selection, selectionTint, widgetShadow } from '../styles';

const MINIMAP_STYLE = css`
  label: MINIMAP_STYLE;
  position: absolute; left: ${MINIMAP_MARGIN}px; bottom: ${MINIMAP_MARGIN}px; z-index: 30;
  background: ${paper};
  border: 1px solid ${ink};
  box-shadow: ${widgetShadow};
  cursor: crosshair; overflow: hidden; touch-action: none;
  svg { display: block; }
`;

const MINIMAP_WINDOW_STYLE = css`
  label: MINIMAP_WINDOW_STYLE;
  fill: ${selectionTint};
  stroke: ${selection};
  stroke-width: 1.5;
  pointer-events: none;
`;

export interface MinimapProps {
  /** The fitted (minimum-zoom) viewport: what the whole map shows. */
  fit: Viewport;
  /** The current viewport: what the window rectangle shows. */
  viewport: Viewport;
  stageW: number;
  stageH: number;
  /** The view should move here. */
  onViewport: (viewport: Viewport) => void;
  /** The drawing, for the thumbnail's viewport and size. */
  children: (viewport: Viewport, width: number, height: number) => React.ReactNode;
}

export function Minimap(props: MinimapProps): React.JSX.Element | null {
  const layout = minimapLayout(props.fit, props.viewport, props.stageW, props.stageH);
  return layout && <ShownMinimap {...props} layout={layout} />;
}

/** The map while it shows, with its drag. */
function ShownMinimap({ fit, viewport, stageW, stageH, onViewport, children, layout }: MinimapProps & { layout: MinimapGeometry }): React.JSX.Element {
  const rootRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const moveTo = (e: React.PointerEvent<HTMLDivElement>): void => {
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect) return;
    onViewport(viewportAtMinimapPoint(viewport, fit, { x: e.clientX - rect.left, y: e.clientY - rect.top }, stageW, stageH));
  };
  const capture = (e: React.PointerEvent<HTMLDivElement>, on: boolean): void => {
    try {
      if (on) e.currentTarget.setPointerCapture?.(e.pointerId);
      else e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch { /* no pointer capture here (jsdom, synthetic events) */ }
  };
  return (
    <div
      ref={rootRef}
      css={MINIMAP_STYLE}
      style={{ width: layout.width, height: layout.height }}
      data-testid='minimap'
      onPointerDown={e => {
        e.preventDefault();
        draggingRef.current = true;
        capture(e, true);
        moveTo(e);
      }}
      onPointerMove={e => {
        if (draggingRef.current && e.buttons) moveTo(e);
      }}
      onPointerUp={e => {
        draggingRef.current = false;
        capture(e, false);
      }}
      onPointerCancel={() => {
        draggingRef.current = false;
      }}
    >
      <svg xmlns='http://www.w3.org/2000/svg' width={layout.width} height={layout.height} viewBox={`0 0 ${layout.width} ${layout.height}`}>
        {children(layout.viewport, layout.width, layout.height)}
        <rect
          css={MINIMAP_WINDOW_STYLE}
          data-testid='minimap-window'
          x={layout.window.x}
          y={layout.window.y}
          width={layout.window.width}
          height={layout.window.height}
        />
      </svg>
    </div>
  );
}
